import React, {useEffect} from "react";
import {Link} from "react-router-dom";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {useForm} from '@tanstack/react-form'
import TableShimmerEffect from "../core/TableShimmerEffect";
import CustomTable2, {ABHI_AMOUNT_LOCK_HINT} from "../core/CustomTable2";
import get from "lodash.get";
import {QueryClient, QueryClientProvider} from 'react-query'
import {getExpenseMappingByCategory} from "../../utils/API";
import {isNotInsurerReadOnlyUser, isVitrayaInternalUser} from "../../constants/UserAuth";
import PcNonPayables from "./PcNonPayables";
import {rowKey} from "../../utils/pcNonPayable";
import {EnvKeys, getEnvItem} from "../../utils/EnvMetaDataUtil";
import {PackageConstructBar} from "./PackageConstruct";

const queryClient = new QueryClient()

function TariffTab(props) {
    const {
        metaData,
        claimData,
        claimId,
        errorTariff,
        loadingTariff,
        tariffTableColumn,
        tariffData,
        dynamicConstruct,
        setTariffData,
        setUpdatedRows,
        setPcUpdated,
        pcSavingRealized,
        setAddedRows,
        saveTariff,
        handleClickOpen,
        handleClose,
        open,
        addedRowIds,
        updatedRows,
        deletedRows,
        deleteRowIdsByRowId,
        // isTariffSaved,
        preFillData,           
        setPreFillData,
        isLoading,
        packageInfo,
        onSwitchPackageView,
        switchingPackageView
    } = props;

  const [selectedMasterCategory, setSelectedMasterCategory] = React.useState(preFillData?.master_category || get(metaData, ['categoryList', 0], {}));
  const [level2And3Data, setLevel2And3Data] = React.useState([]);
  const [level2And3Category, setLevel2And3Category] = React.useState(preFillData?.bill_line_item || '');
  const [isLoadingLevel2And3Data, setIsLoadingLevel2And3Data] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [hasAmountExceededError, setHasAmountExceededError] = React.useState(false);
  // Marking Procedure Construct non-payables is the adjudicate step, but the marked rows have
  // to reach the parent's state before it can be saved - saveTariff reads the tariff list it
  // was rendered with. So the merge is committed first and the save runs on the next render.
  const [pendingAdjudicate, setPendingAdjudicate] = React.useState(false);
  // The ABHI Amount box is shut on any line item the Procedure Construct has cut back or the
  // IRDAI list keeps off the claim. The panel above is where that is undone, so a click on the
  // shut box carries the reviewer back up to it, and the panel's button announces itself on
  // arrival - a pulse to say which control, a callout to say why they are standing in front of
  // it. Long enough to read, then gone; it is a nudge, not a thing to dismiss.
  const pcPanelRef = React.useRef(null);
  const pcAttentionTimer = React.useRef(null);
  const [pcAttention, setPcAttention] = React.useState(false);

  const focusPcNonPayables = React.useCallback(() => {
      const node = pcPanelRef.current;
      if (!node) {
          return;
      }
      node.scrollIntoView({behavior: 'smooth', block: 'center'});
      // Off and on again, so a second click on another shut row replays the pulse from its
      // start rather than landing inside a run that is already half over.
      setPcAttention(false);
      clearTimeout(pcAttentionTimer.current);
      requestAnimationFrame(() => setPcAttention(true));
      pcAttentionTimer.current = setTimeout(() => setPcAttention(false), 5200);
  }, []);

  useEffect(() => () => clearTimeout(pcAttentionTimer.current), []);

  const commitPcRows = React.useCallback((rows) => {
      if (!Array.isArray(rows) || rows.length === 0) {
          return;
      }
      // Matched the way the editor keys its own bookkeeping, but registered by row_id -
      // that is what the save payload is filtered on.
      const byKey = new Map(rows.map(row => [rowKey(row), row]));
      setTariffData(prev => (prev || []).map(item => byKey.get(rowKey(item)) || item));
      setUpdatedRows(prev => {
          const next = new Set(prev);
          rows.forEach(row => next.add(get(row, ['row_id'])));
          return next;
      });
      // This is the one place a Procedure Construct decision enters the tariff list, so it is
      // the one place that can tell the save what kind of edit it is carrying.
      setPcUpdated?.(true);
  }, [setTariffData, setUpdatedRows, setPcUpdated]);

  useEffect(() => {
      if (!pendingAdjudicate) {
          return;
      }
      setPendingAdjudicate(false);
      saveTariff();
  }, [tariffData, pendingAdjudicate, saveTariff]);




    const {Provider, Field, Subscribe, handleSubmit, setFieldValue} = useForm(
        {
            defaultValues: {
                "unit": "",
                "unit_amount": "",
                "tariff_rate": "",
                "irdai_payable": false,
                "allowed_units_by_proc_construct": "",
                "remarks": ""
            },
            onSubmit: async ({value}) => {
                setLoading(true);
                // console.log(value)
                if (get(value, ['unit'], '').length === 0 || get(value, ['unit_amount'], '').length === 0 ||
                    get(value, ['tariff_rate'], '').length === 0 || get(value, ['allowed_units_by_proc_construct'], '').length === 0 ||
                    get(value, ['remarks'], '').length === 0) {
                    setError('Please fill all values')
                    setLoading(false);
                    return
                }

                const getProcedureConstructText = (unit, allowed_units, irdai_payable) => {
                    if (!irdai_payable) {
                        return 'Payable by Procedure Construct'
                    } else if (unit === allowed_units) {
                        return 'Payable by Procedure Construct';
                    } else if (allowed_units === 0) {
                        return 'Non-Payable by Procedure Construct'
                    } else if (unit > allowed_units) {
                        return 'Partially Payable by Procedure Construct'
                    } else {
                        return 'error';
                    }
                }
                let procedureConstructPayableText = getProcedureConstructText(parseFloat(get(value, ['unit'], '0')), parseFloat(get(value, ['allowed_units_by_proc_construct'], '0')), get(value, ['irdai_payable'], true));
                if (procedureConstructPayableText === "error") {
                    setError('Please correct the unit / allowed unit by procedure values')
                    setLoading(false)
                    return
                }
                // A package claim's other view holds row ids this one does not show (the included lines or
                // the package line), so a new row starts above every row id of both views.
                const tariffRowId = packageInfo
                    ? Math.max(...[...tariffData, ...packageInfo.breakdown].map(row => Number(row.row_id) || 0),
                               Number(get(packageInfo, ['summary', 'package_row_id'], 0)) || 0) + 2
                    : tariffData[(tariffData.length)-1].row_id + 2;
                const newRowId = (addedRowIds.size || 0) > 0 ? Math.max(...addedRowIds) + 1 : tariffRowId;
                // const newRowId = (addedRowIds.size || 0) > 0 ? Math.max(...addedRowIds) + 1 : 1001;

                // Do something with form data
                const newRow = {
                    "row_id": newRowId,
                    // "row_id": (addedRowIds.size || 0) > 0 ? Math.max(...addedRowIds) + 1 : 1001,
                    "bill_line_item": level2And3Category,
                    "master_category": get(selectedMasterCategory, ['category'], 'Taxes & Miscellaneous Charges'),
                    "unit": parseFloat(get(value, ['unit'], '0')),
                    "unit_amount": parseFloat(get(value, ['unit_amount'], '0')),
                    "allowed_units_by_proc_construct": parseFloat(get(value, ['allowed_units_by_proc_construct'], '0')),
                    "total_bill_amount": parseFloat(get(value, ['unit_amount'], '0')) * parseFloat(get(value, ['unit'], '0')),
                    "deduction": (parseFloat(get(value, ['unit'], '0')) * parseFloat(get(value, ['unit_amount'], '0'))) - (parseFloat(get(value, ['tariff_rate'], '0')) * parseFloat(get(value, ['allowed_units_by_proc_construct'], '0'))),
                    "tariff_rate": parseFloat(get(value, ['tariff_rate'], '0')),
                    "tariff_rate_for_additional_room_type": parseFloat(get(value, ['tariff_rate'], '0')),
                    "cost_depends_on_room_type": false,
                    "tariff_amount": parseFloat(get(value, ['tariff_rate'], '0')) * parseFloat(get(value, ['allowed_units_by_proc_construct'], '0')),
                    "tariff_amount_actual": parseFloat(get(value, ['tariff_rate'], '0')) * parseFloat(get(value, ['allowed_units_by_proc_construct'], '0')),
                    "admissible_amount": parseFloat(get(value, ['tariff_rate'], '0')) * parseFloat(get(value, ['allowed_units_by_proc_construct'], '0')),
                    "tariff_application_reference_text": get(value, ['bill_line_item']),
                    "irdai_payable": Boolean(get(value, ['irdai_payable'], true)),
                    "procedure_construct_payable": parseFloat(get(value, ['allowed_units_by_proc_construct'], '0')) > 0,
                    "procedure_construct_payable_text": procedureConstructPayableText,
                    "room_type": "",
                    "color": "#ffe9bd",
                    "savings": (parseFloat(get(value, ['unit'], '0')) * parseFloat(get(value, ['unit_amount'], '0'))) - (parseFloat(get(value, ['tariff_rate'], '0')) * parseFloat(get(value, ['allowed_units_by_proc_construct'], '0'))),
                    "admissible_amount_without_procedure_construct": parseFloat(get(value, ['unit'], '0')) * parseFloat(get(value, ['tariff_rate'], '0')),
                    "insurerAmount": parseFloat(get(value, ['tariff_rate'], '0')) * parseFloat(get(value, ['allowed_units_by_proc_construct'], '0')),
                    "remarks": get(value, ['remarks'], ''),
                    "pharmacy_return_item_id":Array.isArray(get(value, ['pharmacy_return_item_id'], [])) && get(value, ['pharmacy_return_item_id'], []) || [],
                    "pharmacy_return_found": Boolean(get(value, ['pharmacy_return_found'],false )) || false,
                    "pharmacy_return_item_threshold": 0,
                    "pharmacy_return_partially_match": false,
                    "admissible_quantity_after_pharmacy_return": 0
                }
                console.log(newRow)
                setTariffData(prev => [...prev, newRow])
                setAddedRows(prev => prev.add(get(newRow, ['row_id'])))

                handleClose()
                setPreFillData(null);
                // setSelectedMasterCategory(get(metaData, ['categoryList', 0], {}))
                    setLoading(false);
                setError('')
            },
        },
    )

    useEffect(() => {
        if (preFillData) {
            Object.entries(preFillData).forEach(([key, value]) => {
                setFieldValue(key, value ?? "");
            });
        }
    }, [preFillData, setFieldValue]);


    const getExpenseCategoryData = async () => {
        setIsLoadingLevel2And3Data(true)
        const response = await getExpenseMappingByCategory(get(selectedMasterCategory, ['expenseMasterCategory']))
        const tempValues = response?.data.map(item => `${item.subServiceNameL3} (${item.serviceNameL2})`)
        setLevel2And3Data(tempValues || [])
        setLevel2And3Category(tempValues[0] || null)
        setIsLoadingLevel2And3Data(false)
    }

    useEffect(() => {
        if (Object.keys(selectedMasterCategory).length > 0) {
            getExpenseCategoryData()
        }
    }, [selectedMasterCategory])

    useEffect(() => {
        setSelectedMasterCategory(get(metaData, ['categoryList', 0], {}))
    }, [metaData])


    return (
        <QueryClientProvider client={queryClient}>
            <Box>
                <Grid
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "10px",
                        margin: "10px",
                    }}
                >
                    <Box ref={pcPanelRef}>
                        <PcNonPayables
                            claimId={claimId}
                            tariffData={tariffData}
                            dynamicConstruct={dynamicConstruct}
                            pcSavingRealized={pcSavingRealized}
                            claimBillAmountRequested={get(claimData, ['claimBillAmountRequested'])}
                            onCommit={commitPcRows}
                            onAdjudicate={() => setPendingAdjudicate(true)}
                            isLoading={isLoading}
                            attention={pcAttention}
                            attentionMessage={ABHI_AMOUNT_LOCK_HINT}
                        />
                    </Box>
                    {/* Edit Tariff is hidden for now - the link and its internal-user gate are
                        kept here ready to be put back. */}
                    {false && isVitrayaInternalUser() && (
                        <Link
                            to={`${getEnvItem(EnvKeys.tariffTestingUrl)}/${claimId}`}
                            target="_blank"
                            style={{
                                border: "1px solid",
                                padding: "8px 10px",
                                textDecoration: "none",
                            }}
                        >
                            {" "}
                            Edit Tariff <EditIcon fontSize={"small"}/>
                        </Link>
                    )}
                </Grid>

                {packageInfo && errorTariff === "" && loadingTariff === false && (
                    <PackageConstructBar
                        packageInfo={packageInfo}
                        onSwitch={onSwitchPackageView}
                        switching={switchingPackageView}
                        hasStagedEdits={addedRowIds.size + updatedRows.size + deletedRows.size > 0}
                        canSwitch={isNotInsurerReadOnlyUser()}
                    />
                )}
                <Paper>
                    <Grid style={{marginTop: "10px"}}>
                        {errorTariff === "" && loadingTariff === false ? (
                            <CustomTable2
                                addedRowIds={addedRowIds}
                                originalData={tariffData || []}
                                setOriginalData={setTariffData}
                                setUpdatedRows={setUpdatedRows}
                                deleteRowIdsByRowId={deleteRowIdsByRowId}
                                handleClickOpen={handleClickOpen}
                                setPreFillData={setPreFillData}
                                isTariff
                                claimId={claimId}
                                onAmountLockedClick={focusPcNonPayables}
                                onValidationChange={setHasAmountExceededError}/>
                        ) : loadingTariff === true && errorTariff === "" ? (
                            <TableShimmerEffect tableColums={tariffTableColumn}/>
                        ) : (
                            errorTariff !== "" &&
                            loadingTariff === false && (
                                <Grid style={{padding: 20, textAlign: "center"}}>
                                    {" "}
                                    <p>{errorTariff}</p>
                                </Grid>
                            )
                        )}
                    </Grid>
                </Paper>
                <Grid
                    style={{
                        marginTop: "10px",
                        marginBottom: "35px",
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'end'
                    }}>
                    {isNotInsurerReadOnlyUser() && (
                        <>
                            <Button sx={{ml: '0.5rem'}} variant="outlined" onClick={() => {setPreFillData(null); handleClickOpen();}}>Add Row</Button>
                            <Button disabled={(addedRowIds.size + updatedRows.size + deletedRows.size === 0) || isLoading || hasAmountExceededError}
                                    sx={{ml: '0.5rem'}}
                                    variant="outlined" onClick={saveTariff}>Save Tariff</Button>
                        </>
                    )}
                    {/* {
            isAbhiInsurer() &&
            <Button disabled={!isTariffSaved} sx={{ ml: '0.5rem' }} variant="outlined" onClick={pushToJarvis}>Push to Jarvis</Button>
          } */}
                </Grid>
                <Dialog
                    open={open}
                    onClose={handleClose}
                >
                    <Provider>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                void handleSubmit()
                            }}
                        >
                            <DialogTitle>Add New Row</DialogTitle>
                            {
                                isLoadingLevel2And3Data &&
                                <DialogContent sx={{width: 400}}>
                                    <CircularProgress/>
                                </DialogContent>
                            }
                            {
                                !isLoadingLevel2And3Data &&
                                <DialogContent sx={{width: 400}}>
                                    {/* <Field
                    name="master_category"
                    children={({ state, handleChange, handleBlur }) => {
                      return ( */}
                                    <Autocomplete
                                        id="free-solo-demo"
                                        sx={{mb: '0.5rem'}}
                                        blurOnSelect
                                        value={selectedMasterCategory}
                                        size='small'
                                        onChange={(e, newValue) => {
                                            setSelectedMasterCategory(newValue)
                                            // handleChange(newValue)
                                        }}
                                        disableClearable
                                        getOptionLabel={(option) => `${option.category}`}
                                        options={metaData?.categoryList}
                                        renderInput={(params) => <TextField
                                            fullWidth
                                            {...params}
                                            size='small'/>}
                                    />
                                    {/* )
                    }}
                  /> */}
                                    {/* <Field
                    name="level_2_3_category"
                    children={({ state, handleChange, handleBlur }) => {
                      return ( */}
                                    <Autocomplete
                                        sx={{mb: '0.5rem'}}
                                        id="free-solo-demo"
                                        blurOnSelect
                                        freeSolo
                                        value={level2And3Category}
                                        size='small'
                                        autoSelect
                                        onChange={(e, newValue) => setLevel2And3Category(newValue)}
                                        disableClearable
                                        // getOptionLabel={(option) => {
                                        //   if (get(option, ['subServiceNameL3']) === null) {
                                        //     return ''
                                        //   }
                                        //   return `${option.subServiceNameL3} (${option.serviceNameL2})`
                                        // }}
                                        options={level2And3Data}
                                        renderInput={(params) => <TextField
                                            fullWidth
                                            {...params}
                                            size='small'/>}
                                    />
                                    {/* )
                    }}
                  /> */}
                                    <Field
                                        name="unit"
                                        children={({state, handleChange, handleBlur}) => {
                                            return (
                                                <TextField
                                                    sx={{mb: '0.5rem'}}
                                                    label="Unit"
                                                    type="number"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    // defaultValue={preFillData?.unit ?? state.value}
                                                    value={preFillData?.unit || state.value ||  ''}
                                                    onChange={(e) => {
                                                        handleChange(e.target.value);
                                                        setPreFillData(prev => ({
                                                            ...prev,
                                                            unit: e.target.value,
                                                            allowed_units_by_proc_construct: e.target.value
                                                        }));
                                                    }}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter No. of Units"
                                                />
                                            )
                                        }}
                                    />
                                    <Field
                                        name="unit_amount"
                                        children={({state, handleChange, handleBlur}) => {
                                            return (
                                                <TextField
                                                    sx={{mb: '0.5rem'}}
                                                    label="Unit Amount"
                                                    type="number"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    // defaultValue={preFillData?.unit_amount ?? state.value}
                                                    value={ preFillData?.unit_amount || state.value ||''}
                                                    onChange={(e) => {
                                                        handleChange(e.target.value);
                                                        setPreFillData(prev => ({
                                                            ...prev,
                                                            unit_amount: e.target.value
                                                        }));
                                                    }}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Amount per Unit"
                                                />
                                            )
                                        }}
                                    />
                                    <Field
                                        name="tariff_rate"
                                        children={({state, handleChange, handleBlur}) => {
                                            return (
                                                <TextField
                                                    sx={{mb: '0.5rem'}}
                                                    label="Tariff Rate"
                                                    type="number"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    // defaultValue={preFillData?.tariff_rate ?? state.value}
                                                    value={preFillData?.tariff_rate || state.value || ''}
                                                    onChange={(e) => {
                                                        handleChange(e.target.value);
                                                        setPreFillData(prev => ({
                                                            ...prev,
                                                            tariff_rate: e.target.value
                                                        }));
                                                    }}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Tariff Amount per Unit by Tariff"
                                                />
                                            )
                                        }}
                                    />
                                    <Field
                                        name="allowed_units_by_proc_construct"
                                        children={({state, handleChange, handleBlur}) => {
                                            return (
                                                <TextField
                                                    sx={{mb: '0.5rem'}}
                                                    label="Allowed Number of Units by Procedure"
                                                    type="number"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    // defaultValue={preFillData?.allowed_units_by_proc_construct ?? state.value}
                                                    value={preFillData?.allowed_units_by_proc_construct || state.value || ''}
                                                    onChange={(e) => {
                                                        handleChange(e.target.value);
                                                        setPreFillData(prev => ({
                                                            ...prev,
                                                            allowed_units_by_proc_construct: e.target.value
                                                        }));
                                                    }}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Units allowed"
                                                />
                                            )
                                        }}
                                    />
                                    <Field
                                        name="irdai_payable"
                                        children={({state, handleChange, handleBlur}) => {
                                            return (
                                                <FormControlLabel
                                                    control={<Checkbox checked={preFillData?.irdai_payable ?? state.value ?? false} onChange={(e) => {
                                                        handleChange(e.target.checked)
                                                        setPreFillData(prev => ({
                                                                ...prev,
                                                                irdai_payable: e.target.value
                                                            }));
                                                    }}/>} label="IRDAI Payable"/>
                                            )
                                        }}
                                    />
                                    <Field
                                        name="remarks"
                                        children={({state, handleChange, handleBlur}) => {
                                            return (
                                                <TextField
                                                    sx={{mb: '0.5rem'}}
                                                    label="Remarks"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    // defaultValue={preFillData?.remarks ?? state.value}
                                                    value={ preFillData?.remarks || state.value || ''}
                                                    onChange={(e) => {
                                                        handleChange(e.target.value);
                                                        setPreFillData(prev => ({
                                                            ...prev,
                                                            remarks: e.target.value
                                                        }));
                                                    }}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Remark"
                                                />
                                            )
                                        }}
                                    />
                                    {error.length > 0 && <Typography sx={{color: 'red'}} variant="subtitle2"
                                                                     gutterBottom>{error}</Typography>}
                                </DialogContent>}
                            <DialogActions>
                                <Button onClick={handleClose}>Cancel</Button>
                                {loading ? (
                                    <CircularProgress size={30} />
                                ) : (
                                    <Subscribe
                                        selector={(state) => [state.canSubmit, state.isSubmitting]}
                                        children={([canSubmit, isSubmitting]) => (
                                            <Button type="submit" disabled={!canSubmit}>
                                                {loading ? <CircularProgress size={30} /> : 'Submit'}
                                            </Button>
                                        )}
                                    />
                                )}
                            </DialogActions>
                        </form>
                    </Provider>
                </Dialog>
            </Box>
        </QueryClientProvider>
    );
}

export default TariffTab;
